import { DragEndEvent, DragMoveEvent } from '@dnd-kit/core'
import { StatementEditorV2IndexNode } from '../../types'

export type DropPosition = 'before' | 'after' | 'inside'
export type DropTarget = { id: string; position: DropPosition }
export type ActiveDropTarget = DropTarget | null
export type IndexPath = number[]

export type ReorderableIndexNode = Omit<StatementEditorV2IndexNode, 'children'> & {
  children: ReorderableIndexNode[]
}

export function parseIndexPath(id: string): IndexPath {
  return id.split('-').map(Number)
}

export function formatIndexPath(path: IndexPath): string {
  return path.join('-')
}

export function deepCopyIndexNodes(nodes: ReorderableIndexNode[]): ReorderableIndexNode[] {
  return nodes.map((node) => ({
    ...node,
    contentRefs: node.contentRefs ? [...node.contentRefs] : undefined,
    children: deepCopyIndexNodes(node.children),
  }))
}

function normalizeIndexNode(node: StatementEditorV2IndexNode): ReorderableIndexNode {
  return {
    id: node.id,
    title: node.title,
    description: node.description,
    type: node.type,
    contentRefs: node.contentRefs ? [...node.contentRefs] : undefined,
    children: (node.children ?? []).map(normalizeIndexNode),
  }
}

export function normalizeIndexData(nodes: StatementEditorV2IndexNode[]): ReorderableIndexNode[] {
  return nodes.map(normalizeIndexNode)
}

function denormalizeIndexNode(node: ReorderableIndexNode): StatementEditorV2IndexNode {
  const children = node.children.map(denormalizeIndexNode)

  return {
    id: node.id,
    title: node.title,
    description: node.description,
    type: node.type,
    contentRefs: node.contentRefs ? [...node.contentRefs] : undefined,
    ...(children.length > 0 ? { children } : {}),
  }
}

export function denormalizeIndexData(nodes: ReorderableIndexNode[]): StatementEditorV2IndexNode[] {
  return nodes.map(denormalizeIndexNode)
}

export function collectExpandedNodeIds(nodes: ReorderableIndexNode[], parentPath: IndexPath = []): string[] {
  const ids: string[] = []

  nodes.forEach((node, index) => {
    const path = [...parentPath, index]
    const id = formatIndexPath(path)
    if (node.children.length > 0) {
      ids.push(id)
      ids.push(...collectExpandedNodeIds(node.children, path))
    }
  })

  return ids
}

export function getItemByPath(nodes: ReorderableIndexNode[], path: IndexPath): ReorderableIndexNode | null {
  let currentItems = nodes

  for (let index = 0; index < path.length; index++) {
    const item = currentItems[path[index]]
    if (!item) {
      return null
    }
    if (index === path.length - 1) {
      return item
    }
    currentItems = item.children
  }

  return null
}

function removeItemAtPath(nodes: ReorderableIndexNode[], path: IndexPath): ReorderableIndexNode | undefined {
  if (path.length === 1) {
    const [removedItem] = nodes.splice(path[0], 1)
    return removedItem
  }

  const parent = getItemByPath(nodes, path.slice(0, -1))
  if (!parent) {
    return undefined
  }

  const [removedItem] = parent.children.splice(path[path.length - 1], 1)
  return removedItem
}

function insertItemAtSiblingPosition(
  nodes: ReorderableIndexNode[],
  targetPath: IndexPath,
  position: DropPosition,
  item: ReorderableIndexNode
) {
  const targetIndex = targetPath[targetPath.length - 1]
  const insertIndex = position === 'before' ? targetIndex : targetIndex + 1

  if (targetPath.length === 1) {
    nodes.splice(insertIndex, 0, item)
    return
  }

  const parent = getItemByPath(nodes, targetPath.slice(0, -1))
  if (!parent) {
    return
  }

  parent.children.splice(insertIndex, 0, item)
}

function getAdjustedTargetPathAfterRemoval(sourcePath: IndexPath, targetPath: IndexPath) {
  const adjustedTargetPath = [...targetPath]
  const sourceParentPath = sourcePath.slice(0, -1)
  const targetIsInSourceSubtree = sourcePath.every((pathPart, index) => pathPart === targetPath[index])
  const targetSharesSourceParent = sourceParentPath.every((pathPart, index) => pathPart === targetPath[index])

  if (
    !targetIsInSourceSubtree &&
    targetSharesSourceParent &&
    sourcePath[sourcePath.length - 1] < targetPath[sourceParentPath.length]
  ) {
    adjustedTargetPath[sourceParentPath.length] -= 1
  }

  return adjustedTargetPath
}

function getNodePathById(nodes: ReorderableIndexNode[], nodeId: string, parentPath: IndexPath = []): IndexPath | null {
  for (let index = 0; index < nodes.length; index++) {
    const node = nodes[index]
    const nodePath = [...parentPath, index]
    if (node.id === nodeId) {
      return nodePath
    }

    const childPath = getNodePathById(node.children, nodeId, nodePath)
    if (childPath) {
      return childPath
    }
  }

  return null
}

function rebaseExpandedItems(
  originalNodes: ReorderableIndexNode[],
  updatedNodes: ReorderableIndexNode[],
  expandedItems: string[]
): string[] {
  const rebasedItems: string[] = []

  expandedItems.forEach((expandedItem) => {
    const originalPath = parseIndexPath(expandedItem)
    const originalNode = getItemByPath(originalNodes, originalPath)
    if (!originalNode) {
      return
    }

    const updatedPath = getNodePathById(updatedNodes, originalNode.id)
    if (updatedPath) {
      rebasedItems.push(formatIndexPath(updatedPath))
    }
  })

  return Array.from(new Set(rebasedItems))
}

export function collectContentRefsFromIndexNodes(nodes: ReorderableIndexNode[]): Set<string> {
  const refs = new Set<string>()

  const walk = (node: ReorderableIndexNode) => {
    node.contentRefs?.forEach((ref) => refs.add(ref))
    node.children.forEach(walk)
  }

  nodes.forEach(walk)
  return refs
}

export function getRemovedContentRefIds(
  originalNodes: ReorderableIndexNode[],
  updatedNodes: ReorderableIndexNode[]
): string[] {
  const originalRefs = collectContentRefsFromIndexNodes(originalNodes)
  const updatedRefs = collectContentRefsFromIndexNodes(updatedNodes)
  return [...originalRefs].filter((ref) => !updatedRefs.has(ref))
}

export function canAddContentToIndexNode(node: ReorderableIndexNode): boolean {
  return node.children.length === 0 && !(node.contentRefs?.length ?? 0)
}

export function nodeHasContentRefs(node: ReorderableIndexNode): boolean {
  return (node.contentRefs?.length ?? 0) > 0
}

/** Only nodes without content refs can be drop targets for re-parenting (drop inside). */
export function canDropInsideIndexNode(node: ReorderableIndexNode): boolean {
  return !nodeHasContentRefs(node)
}

export function isValidIndexDropTarget(nodes: ReorderableIndexNode[], target: DropTarget): boolean {
  if (target.position !== 'inside') {
    return true
  }

  const targetItem = getItemByPath(nodes, parseIndexPath(target.id))
  if (!targetItem) {
    return false
  }

  return canDropInsideIndexNode(targetItem)
}

export function linkContentRefToIndexNode(
  nodes: ReorderableIndexNode[],
  nodeId: string,
  contentRefId: string
): ReorderableIndexNode[] {
  const newNodes = deepCopyIndexNodes(nodes)
  const node = getItemByPath(newNodes, parseIndexPath(nodeId))
  if (!node) {
    return nodes
  }

  node.contentRefs = [...(node.contentRefs ?? []), contentRefId]
  return newNodes
}

export function generateContentId(existingContentIds: Iterable<string>): string {
  let maxNum = 0
  for (const id of existingContentIds) {
    const match = /^content(\d+)$/.exec(id)
    if (match) {
      maxNum = Math.max(maxNum, Number(match[1]))
    }
  }
  return `content${maxNum + 1}`
}

export function deleteIndexNode(
  nodes: ReorderableIndexNode[],
  nodeId: string,
  expandedItems: string[]
): { nodes: ReorderableIndexNode[]; expandedItems: string[] } {
  const newNodes = deepCopyIndexNodes(nodes)
  const path = parseIndexPath(nodeId)
  const removedItem = removeItemAtPath(newNodes, path)

  if (!removedItem) {
    return { nodes, expandedItems }
  }

  const nextExpandedItems = expandedItems.filter((id) => id !== nodeId && !id.startsWith(`${nodeId}-`))

  return { nodes: newNodes, expandedItems: nextExpandedItems }
}

export function moveIndexNode(
  nodes: ReorderableIndexNode[],
  sourceId: string,
  target: DropTarget,
  expandedItems: string[]
): { nodes: ReorderableIndexNode[]; expandedItems: string[] } {
  if (target.id === sourceId || target.id.startsWith(`${sourceId}-`)) {
    return { nodes, expandedItems }
  }

  const newNodes = deepCopyIndexNodes(nodes)
  const sourcePath = parseIndexPath(sourceId)
  const removedItem = removeItemAtPath(newNodes, sourcePath)

  if (!removedItem) {
    return { nodes, expandedItems }
  }

  if (target.position === 'inside') {
    const targetPath = getAdjustedTargetPathAfterRemoval(sourcePath, parseIndexPath(target.id))
    const targetItem = getItemByPath(newNodes, targetPath)
    if (!targetItem || !canDropInsideIndexNode(targetItem)) {
      return { nodes, expandedItems }
    }

    targetItem.children = [...targetItem.children, removedItem]
    const nextExpandedItems = rebaseExpandedItems(nodes, newNodes, expandedItems)
    const updatedTargetPath = getNodePathById(newNodes, targetItem.id)
    if (updatedTargetPath) {
      const updatedTargetId = formatIndexPath(updatedTargetPath)
      if (!nextExpandedItems.includes(updatedTargetId)) {
        nextExpandedItems.push(updatedTargetId)
      }
    }

    return { nodes: newNodes, expandedItems: nextExpandedItems }
  }

  const targetPath = getAdjustedTargetPathAfterRemoval(sourcePath, parseIndexPath(target.id))
  insertItemAtSiblingPosition(newNodes, targetPath, target.position, removedItem)
  return {
    nodes: newNodes,
    expandedItems: rebaseExpandedItems(nodes, newNodes, expandedItems),
  }
}

export function getResolvedDropTarget(
  nodes: ReorderableIndexNode[],
  expandedItems: string[],
  id: string,
  position: DropPosition
): DropTarget {
  const item = getItemByPath(nodes, parseIndexPath(id))

  if (position === 'after' && expandedItems.includes(id) && item?.children.length) {
    return { id: `${id}-0`, position: 'before' }
  }

  return { id, position }
}

export function getDropPosition(event: DragEndEvent | DragMoveEvent): DropPosition | undefined {
  const overRect = event.over?.rect
  const translatedRect = event.active.rect.current.translated

  if (!overRect || !translatedRect) {
    return undefined
  }

  const dropCenterY = translatedRect.top + translatedRect.height / 2
  const topZoneEnd = overRect.top + overRect.height / 3
  const bottomZoneStart = overRect.bottom - overRect.height / 3

  if (dropCenterY < topZoneEnd) {
    return 'before'
  }

  if (dropCenterY > bottomZoneStart) {
    return 'after'
  }

  return 'inside'
}
